<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" sm="6" md="6" v-for="(data, index) in charts[0]" :key="`${index}${data}`">
        <v-row v-if="charts[1][index].title === 'Generation Mix'">
          <v-col cols="12" sm="6">
            <v-select v-model="generationMixYear" label="Year" :items="yearItems" />
          </v-col>
        </v-row>
        <v-row v-if="charts[1][index].title === 'Duck Curve'">
          <v-col cols="12" sm="6">
            <v-select v-model="duckCurveYear" label="Year" :items="yearItems" />
          </v-col>
        </v-row>
        <Plotly
          :data="data"
          :layout="charts[1][index]"
          :toImageButtonOptions="chartDownloadName(caseName, 'PC', charts[1][index].title)"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <ChartStream :data="duckCurve"></ChartStream>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import duckCurveData from "@/assets/DuckCurveParsedDaily.json";
import { chartDownloadName, createAxis, filterLowChartValues } from "@/plugins/utils";
import PCDashboardMixin from "../../mixins/PCDashboard";

export default {
  name: "PCDashboard",
  mixins: [PCDashboardMixin],
  components: {
    Plotly: () => import("@/components/charts/Plotly.vue"),
    ChartStream: () => import("@/components/charts/ChartStream.vue"),
  },
  props: {
    caseOutputs: { type: Object, required: false, default: () => ({}) },
    inputCase: { type: Object, required: false, default: () => ({}) },
    horizontal: { type: Boolean, required: false, default: false },
    logoUrl: {
      type: String,
      required: false,
      default: "https://acelerex.com/wp-content/uploads/2021/01/rex-logo.da3e6933.png",
    },
  },
  data() {
    const defaultLogo = this.logo(this.logoUrl);

    return {
      caseName: this.inputCase.name,
      duckCurve: this.parseDuckCurve(duckCurveData),
      generationMixYear: 0,
      duckCurveYear: 0,
      colorSet: {
        URANIUM: "#FFA500",
        COAL: "#000000",
        HYDRO: "#00008B",
        WIND: "#008000",
        DISTILLATE_FUEL_OIL: "#696969",
        SOLAR_PV: "#FFD700",
        NATURAL_GAS: "#D3D3D3",
        HYDRO_ROR: "#0000FF",
        IMPORT: "rgb(148,103,189)",
      },
      // defaultLogo,
      layouts: [
        // Power & Energy
        {
          dragmode: "pan",
          visible: null,
          title: "Power & Energy",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "MW",
            },
          },
          yaxis2: {
            rangemode: "tozero",
            title: "MWh",
            overlaying: "y",
            side: "right",
          },
          showlegend: true,
          legend: { y: -0.15, orientation: "h" },
          images: [defaultLogo],
        },

        // Average Price
        {
          dragmode: "pan",
          visible: null,
          title: "Average Price",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Price ($/MWh)",
            },
          },
          showlegend: true,
          legend: { y: -0.15, orientation: "h" },
          images: [defaultLogo],
        },

        // Emission
        {
          dragmode: "pan",
          visible: null,
          title: "Emission",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Emission (ton)",
            },
          },
          yaxis2: {
            title: "Emission price ($/ton)",
            overlaying: "y",
            side: "right",
          },
          showlegend: true,
          legend: { y: -0.15, orientation: "h" },
          images: [defaultLogo],
        },

        // Production Costs
        {
          dragmode: "pan",
          visible: null,
          barmode: "relative",
          title: "Production Costs",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Cost ($)",
            },
          },

          showlegend: true,
          legend: { y: -0.15, orientation: "h" },
          images: [defaultLogo],
        },

        // Generation
        {
          dragmode: "pan",
          visible: null,
          title: "Generation",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Generation (GWh)",
            },
          },
          showlegend: true,
          legend: { y: -0.15, orientation: "h" },
          images: [defaultLogo],
        },

        // Generation Mix
        {
          dragmode: "pan",
          visible: null,
          title: "Generation Mix",
          images: [defaultLogo],
        },

        // Storage
        {
          dragmode: "pan",
          visible: null,
          barmode: "relative",
          title: "Storage Revenue",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Revenue ($/yr)",
            },
          },
          showlegend: true,
          legend: { y: -0.15, orientation: "h" },
          images: [defaultLogo],
        },

        // Storage Expansion
        {
          dragmode: "pan",
          barmode: "relative",
          visible: null,
          title: "Storage Expansion",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Capacity (MW)",
            },
          },
          showlegend: true,
          legend: { y: -0.15, orientation: "h" },
          images: [defaultLogo],
        },

        // Renewable Curtailment
        {
          dragmode: "pan",
          visible: null,
          title: "Renewable Curtailment",
          barmode: "stack",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "MWh",
            },
          },
          showlegend: true,
          legend: { y: -0.15, orientation: "h" },
          images: [defaultLogo],
        },

        // Lost Load
        {
          dragmode: "pan",
          visible: null,
          title: "Lost Load",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "MWh",
            },
          },
          showlegend: true,
          legend: { y: -0.15, orientation: "h" },
          images: [defaultLogo],
        },
      ],

      // (will be added to fourth chart data)
      generationCostItem: {
        name: "Generation Cost ($)",
        mode: "lines+markers",
        marker: {
          color: "#000000",
          size: 6,
        },
        line: {
          color: "#000000",
          width: 2,
        },
      },
    };
  },
  computed: {
    yearItems() {
      return this.simulationYearArray.map((year, index) => ({ text: year, value: index }));
    },
    // Data 1
    dataPowerEnergy() {
      return [
        { label: "Peak Demand", data: this.getOutput("demand_peak", this.input), yaxis: "y1" },
        { label: "Annual Energy", data: this.getOutput("demand_annual", this.input), yaxis: "y2" },
      ].map((dataset) => ({
        ...createAxis(dataset.data, this.simulationYearArray),
        name: dataset.label,
        type: "scatter",
        yaxis: dataset.yaxis,
      }));
    },

    // Data 2
    dataAveragePrice() {
      return [{ label: "Average Price", data: this.getOutput("zonal_price", this.output) }].map(
        (dataset) => ({
          ...createAxis(dataset.data, this.simulationYearArray),
          name: dataset.label,
          type: "scatter",
        })
      );
    },

    // Data 3
    dataEmission() {
      return [
        {
          label: "CO2 emission by year",
          data: this.getOutput("co2_emissions", this.output),
          yaxis: "y1",
        },
        {
          label: "CO2 emission price by year",
          data: this.getOutput("co2_price", this.input),
          yaxis: "y2",
        },
      ].map((dataset) => ({
        ...createAxis(dataset.data, this.simulationYearArray),
        name: dataset.label,
        type: "scatter",
        yaxis: dataset.yaxis,
      }));
    },

    // Data 4
    dataProductionCost() {
      const data4 = [
        { label: "VOM Cost ($)", data: this.getOutput("vom_cost", this.output) },
        { label: "Fuel Cost ($)", data: this.getOutput("fuel_cost", this.output) },
        { label: "Emission Cost ($)", data: this.getOutput("co2_emissions_cost", this.output) },
      ].map((dataset) => ({
        ...createAxis(dataset.data, this.simulationYearArray),
        name: dataset.label,
        type: "bar",
      }));
      data4[data4.length] = {
        ...this.generationCostItem,
        ...createAxis(this.getOutput("generation_cost", this.output), this.simulationYearArray),
      };
      return data4;
    },

    // Data 5
    dataGenerationByFuelType() {
      const sum = Object.values(this.generationByFuelType)
        .map((el) => el.reduce((acc, item) => acc + item, 0))
        .reduce((acc, item) => acc + item, 0);
      return Object.values(this.generationByFuelType)
        .map((item, index) => ({
          fillcolor: this.colorSet[Object.keys(this.generationByFuelType)[index]],
          ...createAxis(item, this.simulationYearArray),
          name: this.formatString(Object.keys(this.generationByFuelType)[index]),
          stackgroup: "one",
          mode: "none",
        }))
        .filter(filterLowChartValues(sum * 0.001));
    },

    // Data 6
    dataGenerationByFuelTypeLastYear() {
      const totalValue = Object.keys(this.generationByFuelType)
        .map((el) => this.generationByFuelType[el][this.generationMixYear])
        .reduce((a, b) => a + b);
      const dataObject = Object.keys(this.generationByFuelType)
        .map((source) => ({
          label: source,
          value: this.generationByFuelType[source][this.generationMixYear],
        }))
        .filter((data) => data.value > totalValue * 0.01)
        .reduce(
          (acc, source) => ({
            ...acc,
            [source.label]: source.value,
          }),
          {}
        );

      const data6 = [
        {
          marker: { colors: Object.keys(dataObject).map((source) => this.colorSet[source]) },
          values: Object.values(dataObject),
          labels: Object.keys(dataObject).map((sourceName) => this.formatString(sourceName)),
          type: "pie",
        },
      ];
      return data6;
    },

    // Data 7
    dataStorageEnergyRevenue() {
      const sum = Object.values(this.storageEnergyRevenue)
        .map((el) => el.reduce((acc, item) => acc + item, 0))
        .reduce((acc, item) => acc + item, 0);
      return Object.values(this.storageEnergyRevenue)
        .map((item, index) => ({
          ...createAxis(item, this.simulationYearArray),
          name: Object.keys(this.storageEnergyRevenue)[index].replace(/_/g, " "),
          type: "bar",
        }))
        .filter(filterLowChartValues(sum * 0.001));
    },

    // Data 8
    dataStorageInstalled() {
      const sum = Object.values(this.storageInstalled)
        .map((el) => el.reduce((acc, item) => acc + item, 0))
        .reduce((acc, item) => acc + item, 0);
      return Object.values(this.storageInstalled)
        .map((item, index) => ({
          ...createAxis(item, this.simulationYearArray),
          name: Object.keys(this.storageInstalled)[index].replace(/_/g, " "),
          type: "bar",
        }))
        .filter(filterLowChartValues(sum * 0.001));
    },

    // Renewable Curtailment
    dataRenewableEnergyCourtailment() {
      const sum = Object.values(this.renewableEnergyCourtailment)
        .map((el) => el.reduce((acc, item) => acc + item, 0))
        .reduce((acc, item) => acc + item, 0);
      return Object.values(this.renewableEnergyCourtailment)
        .map((item, index) => ({
          fillcolor: this.colorSet[Object.keys(this.renewableEnergyCourtailment)[index]],
          ...createAxis(item, this.simulationYearArray),
          name: this.formatString(Object.keys(this.renewableEnergyCourtailment)[index]),
          type: "bar",
        }))
        .filter(filterLowChartValues(sum * 0.001));
    },

    datas() {
      return [
        this.dataPowerEnergy,
        this.dataAveragePrice,
        this.dataEmission,
        this.dataProductionCost,
        this.dataGenerationByFuelType,
        this.dataGenerationByFuelTypeLastYear,
        this.dataStorageEnergyRevenue,
        this.dataStorageInstalled,
        this.dataRenewableEnergyCourtailment,
      ];
    },

    // Charts for template
    charts() {
      this.datas.forEach((data, index) => {
        if (data.length > 0) {
          this.layouts[index].visible = true;
        } else {
          this.layouts[index].visible = false;
        }
      });

      return [
        this.datas.filter((chart) => chart.length > 0),
        this.layouts.filter((layout) => layout.visible === true),
      ];
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    filterLowChartValues,
    createAxis,
    chartDownloadName,
    logo(imageSrc) {
      let src = imageSrc;
      if (!src) {
        src = "https://acelerex.com/wp-content/uploads/2021/01/rex-logo.da3e6933.png";
      }

      return {
        xref: "paper",
        yref: "paper",
        x: 1,
        y: 0.1,
        sizex: 0.2,
        sizey: 0.4,
        xanchor: "right",
        yanchor: "middle",
        opacity: 0.8,
        layer: "above",
        source: src,
        alt: "rex-logo",
      };
    },
    parseDuckCurve(data) {
      const image = this.logo(this.logoUrl);
      const years = Object.keys(data[0].value[0]).filter((key) => key.includes("Year"));

      return data.map((day) => ({
        layout: {
          title: `Duck Curve ${day.timestamp.slice(5)}`,
          yaxis: {
            title: "Net Demand (MW)",
          },
          xaxis: {
            title: "Day Hours",
          },
          images: [image],
        },
        data: years.map((year) => ({
          name: year,
          x: day.value.map((v) => v.Hours),
          y: day.value.map((v) => v[year]),
        })),
      }));
    },
  },
};
</script>

<style></style>
